import {
  InputTaskFieldGenericValue,
  TaskField,
  TaskFieldType,
  TaskValue,
  ValidationConfig
} from 'generated/graphql'
import { isNil } from 'lodash-es'
import { FieldValues } from 'react-hook-form'
import { z } from 'zod'

export const getTaskDefaultValues = (values: TaskValue[]) =>
  values.reduce(
    (acc, { key, value }) => {
      const kind = Object.keys(value).find(
        valueKey => value[valueKey as keyof typeof value] !== null
      ) as keyof typeof value

      if (kind === 'listValue') {
        acc[key] = value[kind]?.map(item => {
          const listItemValueKind = Object.keys(item).find(
            listItemValueKey =>
              item[listItemValueKey as keyof typeof item] !== null
          ) as keyof typeof item

          return item[listItemValueKind]
        })

        return acc
      }

      acc[key] = kind ? value[kind] : null
      return acc
    },
    Object.create(null) as Record<string, unknown>
  )

const getFieldGenericValue = (
  value: unknown,
  type: TaskFieldType
): InputTaskFieldGenericValue => {
  switch (type) {
    case TaskFieldType.Boolean:
      if (value !== undefined && typeof value !== 'boolean') {
        throw new Error(`Invalid boolean value: ${value}`)
      }

      return {
        kind: 'boolValue',
        boolValue: value ?? false
      }
    case TaskFieldType.Select:
    case TaskFieldType.Text:
    case TaskFieldType.TextArea:
    case TaskFieldType.Email:
    case TaskFieldType.AutoComplete:
    case TaskFieldType.StringifiedNumber:
    case TaskFieldType.RichText:
      if (value !== undefined && typeof value !== 'string') {
        throw new Error(`Invalid string value: ${value}`)
      }

      return {
        kind: 'stringValue',
        stringValue: value ?? ''
      }
    case TaskFieldType.Number:
      if (value !== undefined && typeof value !== 'number') {
        throw new Error(`Invalid number value: ${value}`)
      }

      return {
        kind: 'numberValue',
        numberValue: value ?? 0
      }
    case TaskFieldType.Date:
      if (value !== undefined && typeof value !== 'number') {
        throw new Error(`Invalid date value: ${value}`)
      }

      return {
        kind: 'stringValue',
        stringValue: String(value || new Date(0).getTime())
      }
    case TaskFieldType.MultiSelect:
    case TaskFieldType.Documents:
    case TaskFieldType.TextList:
      if (value !== undefined && !Array.isArray(value)) {
        throw new Error(`Invalid list value: ${value}`)
      }

      return {
        kind: 'listValue',
        listValue:
          value?.map(item => {
            if (typeof item !== 'string') {
              throw new Error(`Invalid list item value: ${item}`)
            }
            return {
              kind: 'stringValue',
              stringValue: item
            }
          }) ?? []
      }
    default:
      throw new Error(`Unknown input type: ${type}`)
  }
}

const getFieldValue = (
  field: { key: string; value: unknown },
  fields: TaskField[]
) => {
  const { key } = field

  const formField = fields.find(({ id }) => id === key)

  if (!formField) {
    throw new Error(`Field with key ${field.key} not found`)
  }

  return getFieldGenericValue(field.value, formField.type)
}

export const getTaskSubmitData = (data: FieldValues, fields: TaskField[]) =>
  Object.entries(data).map(([key, value]) => ({
    key,
    value: getFieldValue({ key, value }, fields)
  }))

export const getValidationSchema = (
  fields?: TaskField[] | null,
  validationConfigs?: ValidationConfig[] | null
) => {
  if (!fields?.length) {
    return null
  }

  if (!validationConfigs?.length) {
    return null
  }

  const schemaObject = validationConfigs.reduce((acc, validationConfig) => {
    const { key, required, minLength, maxLength, pattern, minValue, maxValue } =
      validationConfig

    const field = fields.find(({ id }) => id === key)

    if (!field) {
      return acc
    }

    let fieldSchema

    switch (field.type) {
      case 'boolean':
        fieldSchema = z.boolean()

        break
      case 'date':
        fieldSchema = z.number()

        if (!isNil(minValue)) {
          fieldSchema = fieldSchema.min(minValue)
        }

        if (!isNil(maxValue)) {
          fieldSchema = fieldSchema.max(maxValue)
        }

        fieldSchema = fieldSchema.refine(val => {
          const date = new Date(val)
          return !Number.isNaN(date.getTime())
        }, 'Invalid date format')

        break
      case 'email':
        fieldSchema = z.string().email()

        if (!isNil(minLength)) {
          fieldSchema = fieldSchema.min(minLength)
        }

        if (!isNil(maxLength)) {
          fieldSchema = fieldSchema.max(maxLength)
        }

        break
      case 'documents':
      case 'text_list':
      case 'multi_select':
        fieldSchema = z.array(z.string())

        if (!isNil(minLength) || required) {
          fieldSchema = fieldSchema.min(minLength || 1)
        }

        if (!isNil(maxLength)) {
          fieldSchema = fieldSchema.max(maxLength)
        }

        break
      case 'number':
        fieldSchema = z.number()

        if (!isNil(minValue)) {
          fieldSchema = fieldSchema.min(minValue)
        }

        if (!isNil(maxValue)) {
          fieldSchema = fieldSchema.max(maxValue)
        }

        break
      case 'stringified_number':
        fieldSchema = z.string().transform(val => {
          const parsed = Number(val)
          if (Number.isNaN(parsed)) {
            throw new Error('Invalid number format')
          }
          return parsed
        })

        // eslint-disable-next-line no-case-declarations
        let internalSchema = z.number()

        if (!isNil(minValue)) {
          internalSchema = internalSchema.min(minValue)
        }

        if (!isNil(maxValue)) {
          internalSchema = internalSchema.max(maxValue)
        }

        fieldSchema = fieldSchema.pipe(
          internalSchema.transform(val => String(val))
        )

        fieldSchema.transform(val => String(val))

        break
      case 'auto_complete':
      case 'select':
      case 'text':
      case 'text_area':
        fieldSchema = z.string()

        if (!isNil(minLength) || required) {
          fieldSchema = fieldSchema.min(minLength || 1)
        }

        if (!isNil(maxLength)) {
          fieldSchema = fieldSchema.max(maxLength)
        }

        if (!isNil(pattern)) {
          fieldSchema = fieldSchema.regex(new RegExp(pattern))
        }

        break
      default:
        throw new Error(`Unknown input type: ${field?.type}`)
    }

    if (!required) {
      fieldSchema = fieldSchema.nullish()
    }

    acc[key] = fieldSchema

    return acc
  }, Object.create(null))

  return z.object(schemaObject)
}
